import * as React from 'react'

import { inject, MobXProviderContext, observer } from 'mobx-react'

import { PendingRecordPanel } from '~/client/src/desktop/components/Resource/compound/PendingRecordPanel'
import { useResource } from '~/client/src/desktop/components/Resource/context'
import { useResourceDeleteAction } from '~/client/src/desktop/components/Resource/useResourceDeleteAction'
import { navigateToProjectTimeSettings } from '~/client/src/desktop/components/ResourceControls/navigateToProjectTimeSettings'
import { EquipmentResourceEditBody } from '~/client/src/shared/components/EquipmentResourceEdit/EquipmentResourceEditBody'
import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { useResolvedTemplate } from '~/client/src/shared/components/FormListSelector/useResolvedTemplate'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import {
  LinkedResourceEdit,
  useLinkedResourceEditor,
} from '~/client/src/shared/components/ResourceLink'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { useEquipmentSensorLinkField } from '~/client/src/shared/hooks/useEquipmentSensorLinkField'
import { extractControlFields } from '~/client/src/shared/hooks/useResourceControls'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import InitialState from '~/client/src/shared/stores/InitialState'

interface IProps {
  state?: InitialState
  /** Which section opens expanded by default (e.g. 'controls' from the controls table). */
  defaultExpandedSectionId?: string
}

const EquipmentEditPanel: React.FC<IProps> = ({
  state,
  defaultExpandedSectionId,
}) => {
  const {
    state: { selectedRecordId, chosenTemplateId, records },
    actions: { closeEditPanel, save, setChosenTemplateId },
    meta: { resourceTypeId, runtimeContext, schema, controlCommands },
  } = useResource()

  const projectId = state?.activeProject?.id || ''

  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)

  const stores = React.useContext(MobXProviderContext) as any
  const isAdmin = UserProject.isAdmin(
    stores.state?.user,
    stores.userProjectsStore,
  )

  // Desktop can navigate to project settings; the provider gates this to admins,
  // and mobile omits it so the inherited-hours chip's link stays inactive there.
  const onEditProjectHours = React.useCallback(
    () => navigateToProjectTimeSettings(stores.projectSetUpPageStore),
    [stores.projectSetUpPageStore],
  )

  const isCreating = selectedRecordId === 'new'

  const {
    effectiveTemplateId,
    templates,
    loading: templatesLoading,
  } = useResolvedTemplate({
    enabled: isCreating,
    chosenTemplateId,
    templates: schema.templates,
    loading: schema.loading,
  })

  const selectedRecord = React.useMemo(() => {
    if (!selectedRecordId || selectedRecordId === 'new') return null
    return records.find(r => r.id === selectedRecordId) || null
  }, [selectedRecordId, records])

  const editableRecord = React.useMemo(() => {
    if (!selectedRecord) return null
    return {
      ...selectedRecord,
      code: selectedRecord.code ?? null,
      stxResourceId: selectedRecord.stxResourceId ?? null,
      isDeleted: selectedRecord.isDeleted ?? false,
      templateId: selectedRecord.templateId ?? null,
    }
  }, [selectedRecord])

  const linkField = useEquipmentSensorLinkField({
    projectId,
    sourceRecordId: selectedRecordId === 'new' ? null : selectedRecordId,
    sourceRecord: selectedRecord,
  })

  const { openedLinkedRecordId, openLinkedRecord, closeLinkedRecord } =
    useLinkedResourceEditor(linkField.linkRootProps.linkedItem)

  const qrCodeUrl = React.useMemo(() => {
    if (!selectedRecordId || selectedRecordId === 'new') return null
    return `${window.location.origin}/resource/${resourceTypeId}/${selectedRecordId}`
  }, [selectedRecordId, resourceTypeId])

  const { onDelete, confirmDialog } = useResourceDeleteAction(selectedRecordId)

  if (!selectedRecordId) {
    if (isCreatingNewForm) setIsCreatingNewForm(false)
    return null
  }

  if (!isCreating && !selectedRecord) return <PendingRecordPanel />

  if (isCreating && templatesLoading) {
    return (
      <SidePanel>
        <div className="tw:flex tw:items-center tw:justify-center tw:size-full tw:bg-background">
          <Spinner size="md" />
        </div>
      </SidePanel>
    )
  }

  if (isCreating && !effectiveTemplateId) {
    if (isCreatingNewForm) {
      return (
        <SidePanel>
          <FormPropertiesSidebar
            mode="create"
            resourceTypeId={resourceTypeId}
            onClose={closeEditPanel}
            onBackToPicker={() => setIsCreatingNewForm(false)}
            resourceSchema={schema}
            onCreated={newTemplateId => {
              setIsCreatingNewForm(false)
              setChosenTemplateId(newTemplateId)
            }}
          />
        </SidePanel>
      )
    }
    return (
      <SidePanel>
        <FormListSelector
          title={Localization.translator.selectEquipmentForm}
          titleVariant="panel"
          resourceTypeId={resourceTypeId}
          onSelect={setChosenTemplateId}
          onClose={closeEditPanel}
          onCreateNewForm={
            isAdmin ? () => setIsCreatingNewForm(true) : undefined
          }
          templates={templates}
          loading={schema.loading}
        />
      </SidePanel>
    )
  }

  const isCreationMode = isCreating

  return (
    <>
      <SidePanel testId="resource-edit-panel">
        <EquipmentResourceEditBody
          recordId={selectedRecordId}
          record={editableRecord}
          controlCommands={controlCommands}
          controlFields={extractControlFields(selectedRecord?.root ?? {})}
          onEditProjectHours={onEditProjectHours}
          templateId={effectiveTemplateId || undefined}
          resourceTypeId={resourceTypeId}
          runtimeContext={runtimeContext}
          projectId={projectId}
          onClose={closeEditPanel}
          onSave={save}
          keepOpenOnCreate={true}
          onTemplateChange={setChosenTemplateId}
          qrCodeUrl={qrCodeUrl}
          isCreationMode={isCreationMode}
          linkField={linkField}
          onOpenLinked={openLinkedRecord}
          onDelete={onDelete}
          defaultExpandedSectionId={defaultExpandedSectionId}
          resourceSchema={schema}
          formSelector={
            <ResourceEdit.FormSelector
              resourceTypeId={resourceTypeId}
              templates={templates}
              currentTemplateId={effectiveTemplateId}
            />
          }
        />
      </SidePanel>
      <LinkedResourceEdit
        open={!!openedLinkedRecordId}
        recordId={openedLinkedRecordId}
        projectId={projectId}
        resourceTypeId={linkField.targetResourceTypeId}
        onClose={closeLinkedRecord}
        shell={children => <SidePanel zIndex={110}>{children}</SidePanel>}
      />
      {confirmDialog}
    </>
  )
}

export default inject('state')(observer(EquipmentEditPanel))
